import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Form, Button, Card, Spinner, Nav, InputGroup, Modal, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import {
  createCustomer, fetchApplicableProducts, fetchPaymentMethodsByEntity, // <-- Imported new API
  fetchUnassignedDevices,
  fetchUnassignedSims, checkCustomerEmail, checkRecordId,
  fetchEntityDropDownList,
  fetchClientDropDownList,
} from '../services/api';
import { getCurrentUser } from '../services/authService'; // Import auth service
import SearchableDropdown from '../components/SearchableDropdown';
import useDebounce from '../Hooks/useDebounce';
import { USER_ROLES } from '../lib/utils/constant';
import {
  sanitizeLocationFieldInput,
  validateLocationField,
} from '../lib/utils/validation';

const CreditCardIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 16 16"><path d="M0 4a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V4zm2-1a1 1 0 0 0-1 1v1h14V4a1 1 0 0 0-1-1H2zm13 4H1v5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V7z" /></svg>;


// --- Helper: Stepper UI ---
const Stepper = ({ currentStep }) => {
  const steps = ['Profile', 'Device', 'Order Information'];
  return (
    <Nav className="mb-4" fill>
      {steps.map((step, index) => (
        <Nav.Item key={index}>
          <div className={`text-center ${index + 1 <= currentStep ? 'text-primary' : 'text-muted'}`}>
            <div
              className={`p-2 border rounded-circle d-inline-block ${index + 1 <= currentStep ? 'border-primary' : 'border-secondary'} ${index + 1 === currentStep ? 'bg-primary text-white' : 'bg-light'}`}
              style={{ width: '40px', height: '40px', lineHeight: '1.4' }}
            >
              {index + 1}
            </div>
            <div className={`mt-2 ${index + 1 <= currentStep ? 'fw-bold' : ''}`}>{step}</div>
          </div>
        </Nav.Item>
      ))}
    </Nav>
  );
};

function AddCustomer() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);

  // --- Main Form State ---
  const [formData, setFormData] = useState({
    // Step 1: Personal Info
    firstName: '', lastName: '', dob: '', gender: '', email: '', zipcode: '', city: '', state: '', country: '',
    // Step 2: Assignment
    deviceImei: '', simNo: '', entity: '', client: '', recordId: '',
    // Step 3: Order Info
    productId: '', frequencyId: '', price: '', currency: '',
    // Step 3: Payment Info
    paymentMethodId: '', // <-- Added ID field
    cardName: '', cardNumber: '', cardExpiry: '', cardCvc: '',
    // Step 3: Billing Address
    billingAddress: '', billingCity: '', billingState: '', billingZip: '', billingCountry: ''
  });

  const [isFetchingZip, setIsFetchingZip] = useState(false);
  const [, setIsFetchingBillingZip] = useState(false);
  const [validated, setValidated] = useState(false);

  // Product & Entity State
  const [entitiesList, setEntitiesList] = useState([]); // Store API response
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showRecordId, setShowRecordId] = useState(false); // <-- State to toggle Customer ID field

  // Payment State
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loadingPayment, setLoadingPayment] = useState(false);
  const [errors, setErrors] = useState({});

  // --- Role Based Visibility State ---
  const [showEntityDropdown, setShowEntityDropdown] = useState(true);
  const [disableEntityDropdown, setDisableEntityDropdown] = useState(false);
  const [showClientDropdown, setShowClientDropdown] = useState(true);
  const [availableClients, setAvailableClients] = useState([]); // State for filtered clients

  // --- Loader & Modal State ---
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Debouncers for both zipcodes
  const debouncedZipcode = useDebounce(formData.zipcode, 500);
  const debouncedBillingZip = useDebounce(formData.billingZip, 500);
  // Helper for Date Input max value
  const todayDate = new Date().toISOString().split("T")[0];


  // --- 1. Load Initial Data (User & Entities) ---
  useEffect(() => {
    const user = getCurrentUser();
    if (!user) return;

    const roleCode = user.role?.code || user.roleCode || '';
    const accountTypeRaw = user.entityMaster?.accountType || user.entityMaster?.account_type || user.accountType || '';
    const accountType = String(accountTypeRaw).toUpperCase();
    const userEntityId = user.entityMaster?.id ?? user.entityMasterId ?? user.entityId;
    const parentEntityId = user.entityMaster?.parent_id ?? user.parentEntityMasterId ?? user.parentEntityId;
    const userEntityName = user.entityMaster?.name || user.entityMaster?.entityName || '';
    const entityIdValue = userEntityId ? userEntityId.toString() : '';
    const parentEntityIdValue = parentEntityId ? parentEntityId.toString() : '';
    const isClientAccount = accountType === USER_ROLES.CLIENT;
    // --- Role Logic ---
    // Case 1: System Admin (SYS_ADMIN)
    if (roleCode === 'SYS_ADMIN' || roleCode === 'SUPER_ADMIN') {
      setShowEntityDropdown(true);
      setDisableEntityDropdown(false);
      setShowClientDropdown(true);
      setShowRecordId(false);
    }
    // Case 2: Client Account
    else if (isClientAccount) {
      // Hide entity dropdown and prefill client
      console.log('isClientAccount',isClientAccount);
      setShowEntityDropdown(false);
      setDisableEntityDropdown(true);
      setShowClientDropdown(true);

      setFormData(prev => ({
        ...prev,
        client: entityIdValue,
        entity: parentEntityIdValue
      }));
      if (entityIdValue) {
        setAvailableClients(prev => {
          if (prev.some(c => String(c.id) === entityIdValue)) return prev;
          return [...prev, { id: parseInt(entityIdValue, 10), name: userEntityName || 'Current Client' }];
        });
      }
      if (user.entityMaster) {
        const isEnabled = user.entityMaster.enableEntityAssignedRecordId === true;
        setShowRecordId(isEnabled);
      } else {
        setShowRecordId(false);
      }
    }
    // Case 3: Entity Account
    else if (accountType === USER_ROLES.ENTITY) {
      // Show entity dropdown as read-only and prefilled
      setShowEntityDropdown(true);
      setDisableEntityDropdown(true);
      setShowClientDropdown(true);
      setFormData(prev => ({ ...prev, entity: entityIdValue }));
      if (entityIdValue) {
        setEntitiesList(prev => {
          if (prev.some(e => String(e.id) === entityIdValue)) return prev;
          return [...prev, { id: parseInt(entityIdValue, 10), name: userEntityName || 'Current Entity' }];
        });
      }
      setShowRecordId(user.entityMaster?.enableEntityAssignedRecordId === true);
    }

    setLoadingPayment(false);

    // // Fetch Entity List from API (Only needed if user can select entities)
    const fetchEntityData = async () => {
      const response = await fetchEntityDropDownList();
      if (response.data && response.data.success && response.data.data?.items) {
        const fetchedItems = [...response.data.data.items];
        // Keep current entity selectable even if API omits it for this role.
        if (accountType === USER_ROLES.ENTITY && entityIdValue) {
          const hasCurrentEntity = fetchedItems.some(e => String(e.id) === entityIdValue);
          if (!hasCurrentEntity) {
            fetchedItems.push({
              id: parseInt(entityIdValue, 10),
              name: userEntityName || 'Current Entity'
            });
          }
        }
        setEntitiesList(fetchedItems);
      }
    };
    if (
      user.role.code === USER_ROLES.SYS_ADMIN ||
      user.role.code === USER_ROLES.SYS_USER 
    ) {
      fetchEntityData();
    } else if (accountType === USER_ROLES.ENTITY && entityIdValue) {
      setFormData(prev => ({ ...prev, entity: entityIdValue }));
    } else if (accountType === USER_ROLES.CLIENT && entityIdValue) {
      setFormData(prev => ({ ...prev, client: entityIdValue, entity: parentEntityIdValue }));
    }
  }, []);

  // --- 2. Logic: Update Available Clients & Check Record ID Config (For Admins selecting from dropdowns) ---
  useEffect(() => {
    const user = getCurrentUser();
    const roleCode = user?.role?.code || user?.roleCode || '';
    const accountTypeRaw = user?.entityMaster?.account_type || user?.entityMaster?.accountType || user?.accountType;
    const accountType = String(accountTypeRaw || '').toUpperCase();

    if (accountType === USER_ROLES.CLIENT || roleCode === 'CLIENT_ADMIN' || (roleCode === 'ADMIN' && accountType === 'CLIENT')) {
      if (user?.entityMaster) {
        const isEnabled = user.entityMaster.enableEntityAssignedRecordId === true;
        setShowRecordId(isEnabled);
      }
      return; // Skip rest of effect
    }
    // Only run if dropdowns are visible/used
    if (!showEntityDropdown && !showClientDropdown) return;

    if (formData.entity && (accountType !== USER_ROLES.CLIENT)) {
      const selectedEntityId = parseInt(formData.entity, 10);
      // Find selected entity object from loaded list (for SYS/SUPER flows)
      const selectedEntity = entitiesList.find(e => e.id === selectedEntityId);

      const fetchClients = async () => {
        const response = await fetchClientDropDownList(selectedEntityId);
        if (response.data && response.data.success && response.data.data?.items) {
          setAvailableClients(response.data.data.items);
        } else {
          setAvailableClients([]);
        }
      };
      fetchClients();

      // If only entity selected (no client yet), check entity config when list is available
      if (!formData.client && selectedEntity) {
        setShowRecordId(selectedEntity.enableEntityAssignedRecordId || false);
      }
    } else {
      setAvailableClients([]);
    }

  }, [formData.entity, entitiesList, showEntityDropdown, showClientDropdown,formData.client]);

  // --- 3. Logic: Check Record ID Config when Client Changes (For dropdown users) ---
  useEffect(() => {
    const user = getCurrentUser();
    const accountTypeRaw = user?.entityMaster?.accountType || user?.accountType;
    const accountType = String(accountTypeRaw || '').toUpperCase();

    if (accountType === USER_ROLES.CLIENT) {
      if (user?.entityMaster) {
        const isEnabled = user.entityMaster.enableEntityAssignedRecordId === true;
        setShowRecordId(isEnabled);
      }
      return; // Skip rest of effect
    }
    // Only run if dropdowns are active
    if (!showClientDropdown) return;

    if (formData.client && availableClients.length > 0) {
      const selectedClient = availableClients.find(c => c.id === parseInt(formData.client));
      if (selectedClient) {
        // Override with client specific config from the dropdown object
        setShowRecordId(selectedClient.enableEntityAssignedRecordId || false);
      }
    } else {
      // If Entity Admin logged in and entity is preset but client cleared
      if (!formData.client && formData.entity) {
        const selectedEntity = entitiesList.find(e => e.id === parseInt(formData.entity));
        if (selectedEntity) {
          setShowRecordId(selectedEntity.enableEntityAssignedRecordId || false);
        } else {
          setShowRecordId(false);
        }
      } else {
        setShowRecordId(false);
      }
    }
  }, [formData.client, availableClients, showClientDropdown, formData.entity, entitiesList]);


  // --- Logic: Fetch Payment Methods only on Order Information (step 3) ---
  useEffect(() => {
    if (step !== 3) return;

    const targetId = formData.client ? formData.client : null;
    if (!targetId) {
      setPaymentMethods([]);
      setFormData((prev) => ({ ...prev, paymentMethodId: '' }));
      setLoadingPayment(false);
      return;
    }

    let cancelled = false;
    setLoadingPayment(true);

    const loadPaymentMethods = async () => {
      try {
        const response = await fetchPaymentMethodsByEntity(targetId);
        if (cancelled) return;

        if (response.data && response.data.success) {
          const methods = response.data.data?.items || response.data.data || [];
          const list = Array.isArray(methods) ? methods : [];
          setPaymentMethods(list);

          const preferred =
            list.find((m) => String(m.id) === String(formData.paymentMethodId))
            || list.find((m) => m.isDefault)
            || list[0]
            || null;

          setFormData((prev) => ({
            ...prev,
            paymentMethodId: preferred ? preferred.id : '',
          }));
        } else {
          setPaymentMethods([]);
          setFormData((prev) => ({ ...prev, paymentMethodId: '' }));
        }
      } catch (error) {
        if (cancelled) return;
        console.error('Error loading payment methods:', error);
        setPaymentMethods([]);
        setFormData((prev) => ({ ...prev, paymentMethodId: '' }));
      } finally {
        if (!cancelled) setLoadingPayment(false);
      }
    };

    loadPaymentMethods();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, formData.client]);

  useEffect(() => {
    const targetEntityId = formData.client ? formData.client : null;

    const loadProducts = async () => {
      if (targetEntityId) {
        try {
          const response = await fetchApplicableProducts(targetEntityId);
          const productList = response.data?.data?.products || [];
          setProducts(productList);
        } catch (error) {
          console.error("Error fetching products:", error);
          // Fallback to empty or handle error
          setProducts([]);
        }
      } else {
        setProducts([]);
      }
    };
    loadProducts();
  }, [formData.entity, formData.client]); // Re-run if entity or client changes

  // --- Logic: Zipcode Autofill ---
  const fetchZipcodeData = useCallback(async (zip, isBilling = false) => {
    if (!zip || zip.length < 5) return;

    const setSpinner = isBilling ? setIsFetchingBillingZip : setIsFetchingZip;
    setSpinner(true);

    try {
      const response = await fetch(`https://api.zippopotam.us/us/${zip}`);
      if (!response.ok) throw new Error('Zipcode not found');
      const data = await response.json();
      const place = data.places[0];

      if (isBilling) {
        setFormData(prev => ({
          ...prev,
          billingCity: place['place name'],
          billingState: place['state abbreviation'],
          billingCountry: data['country abbreviation']
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          city: place['place name'],
          state: place['state abbreviation'],
          country: data['country abbreviation']
        }));
      }
    } catch (error) {
      console.error("Zipcode fetch error:", error);
    } finally {
      setSpinner(false);
    }
  }, []);

  // Effect for Personal Zip
  useEffect(() => {
    if (debouncedZipcode) fetchZipcodeData(debouncedZipcode, false);
  }, [debouncedZipcode, fetchZipcodeData]);

  // Effect for Billing Zip
  useEffect(() => {
    if (debouncedBillingZip) fetchZipcodeData(debouncedBillingZip, true);
  }, [debouncedBillingZip, fetchZipcodeData]);

  // --- Real-time Validation Helper ---
  const validateField = (name, value) => {
    let error = "";
    const trimmedValue = String(value || '').trim();

    switch (name) {
      case 'email':
        if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) error = "Enter a valid email address.";
        break;

      case 'deviceImei':
        if (value && !/^\d{15}$/.test(value)) error = "Enter 15 digits.";
        break;

      case 'simNo':
        if (value && !/^\d{19,20}$/.test(value)) error = "Enter 19 or 20 digits.";
        break;

      case 'zipcode':
        error = trimmedValue ? (validateLocationField('zipCode', value) || "") : "";
        break;

      case 'city':
        error = trimmedValue ? (validateLocationField('city', value) || "") : "";
        break;

      case 'state':
        error = trimmedValue ? (validateLocationField('state', value) || "") : "";
        break;

      case 'country':
        error = trimmedValue ? (validateLocationField('country', value) || "") : "";
        break;

      default:
        break;
    }
    return error;
  };

  // --- Handlers ---
  const handleChange = (e) => {
    const { name, value } = e.target;
    let newValue = value;

    // --- ENFORCE NUMERIC ONLY INPUT ---
    if (name === 'cardNumber' || name === 'cardCvc') {
      // Strip everything that is not a digit
      newValue = value.replace(/\D/g, '');
    }

    if (name === 'cardExpiry') {
      // Strip non-digits
      const digits = value.replace(/\D/g, '');
      // Auto-insert slash after 2 digits
      if (digits.length >= 3) {
        newValue = `${digits.slice(0, 2)}/${digits.slice(2, 4)}`;
      } else {
        newValue = digits;
      }
      // Limit total length to 5 chars (MM/YY)
      if (newValue.length > 5) newValue = newValue.slice(0, 5);
    }

    if (['zipcode', 'city', 'state', 'country'].includes(name)) {
      const locationFieldName = name === 'zipcode' ? 'zipCode' : name;
      newValue = sanitizeLocationFieldInput(locationFieldName, value);
    }
    // ----------------------------------

    setFormData(prev => ({ ...prev, [name]: newValue }));

    // Validate using the new value
    const errorMsg = validateField(name, newValue);
    setErrors(prev => ({ ...prev, [name]: errorMsg }));
  };

  // Handler for Searchable Dropdown selections (Step 2)
  const handleDropdownSelect = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Validate the selected value immediately
    const errorMsg = validateField(field, value);
    setErrors(prev => ({ ...prev, [field]: errorMsg }));
  };

  const handleProductChange = (e) => {
    const pId = parseInt(e.target.value);
    const product = products.find(p => p.id === pId);
    setSelectedProduct(product);
    setFormData(prev => ({ ...prev, productId: pId, frequencyId: '', price: '', currency: '' }));
  };

  const handleFrequencyChange = (e) => {
    const fId = parseInt(e.target.value);
    const frequency = selectedProduct?.frequencies.find(f => f.id === fId);
    setFormData(prev => ({
      ...prev,
      frequencyId: fId,
      price: frequency?.pricing?.price || '',
      currency: frequency?.pricing?.currency || ''
    }));
  };

  // --- Step Validation Helper ---
  const validateCurrentStep = () => {
    const newErrors = {};
    let isValid = true;

    if (step === 1) {
      const emailErr = validateField('email', formData.email);
      if (emailErr) { newErrors.email = emailErr; isValid = false; }

      const zipErr = validateField('zipcode', formData.zipcode);
      if (zipErr) { newErrors.zipcode = zipErr; isValid = false; }

      const cityErr = validateField('city', formData.city);
      if (cityErr) { newErrors.city = cityErr; isValid = false; }

      const stateErr = validateField('state', formData.state);
      if (stateErr) { newErrors.state = stateErr; isValid = false; }

      const countryErr = validateField('country', formData.country);
      if (countryErr) { newErrors.country = countryErr; isValid = false; }
    }

    if (step === 2) {
      // Validate Searchable Dropdowns
      if (!formData.deviceImei) { newErrors.deviceImei = "Required"; isValid = false; }
      else {
        const imeiErr = validateField('deviceImei', formData.deviceImei);
        if (imeiErr) { newErrors.deviceImei = imeiErr; isValid = false; }
      }

      if (!formData.simNo) { newErrors.simNo = "Required"; isValid = false; }
      else {
        const simErr = validateField('simNo', formData.simNo);
        if (simErr) { newErrors.simNo = simErr; isValid = false; }
      }

      // Validate Entity/Client selection if they are visible
      if (showEntityDropdown && !formData.entity) {
        // Usually HTML required attribute handles this, but explicit check helps
        isValid = false;
      }
      if (showClientDropdown && !formData.client) {
        isValid = false;
      }
      if (showRecordId && !formData.recordId) {
        isValid = false;
      }
    }

    // Step 3 Validation: Ensure a payment card is selected
    if (step === 3) {
      if (!formData.paymentMethodId) {
        isValid = false;
      }
    }

    setErrors(prev => ({ ...prev, ...newErrors }));
    return isValid;
  };

  // --- Submit ---
  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = e.currentTarget;

    if (form.checkValidity() === false) {
      e.stopPropagation();
      setValidated(true);
      return;
    }

    if (!validateCurrentStep()) {
      e.stopPropagation();
      setValidated(true);
      return;
    }

    if (step < 3) {
      // --- NEW: Check Email on Step 1 ---
      if (step === 1) {
        setIsSubmitting(true);
        try {
          const response = await checkCustomerEmail(formData.email);
          if (response.data && response.data.success) {
            setStep(s => s + 1);
            setValidated(false);
          } else {
            throw new Error(response.data?.message || "Email validation failed.");
          }
        } catch (err) {
          console.error("Email Check Failed:", err);
          const msg = err.response?.data?.message || "This email address is already in use.";
          setErrorMessage(msg);
          setShowErrorModal(true);
        } finally {
          setIsSubmitting(false);
        }
      } else if (step === 2) {
        // --- NEW: Check Record ID on Step 2 ---
        if (showRecordId && formData.recordId) {
          setIsSubmitting(true);
          try {
            const response = await checkRecordId(formData.recordId);
            if (response.data && response.data.success) {
              setStep(s => s + 1);
              setValidated(false);
            } else {
              throw new Error(response.data?.message || "Record ID validation failed.");
            }
          } catch (err) {
            console.error("Record ID Check Failed:", err);
            const msg = err.response?.data?.message || "This Record ID is already in use.";
            setErrorMessage(msg);
            setShowErrorModal(true);
          } finally {
            setIsSubmitting(false);
          }
        } else {
          setStep(s => s + 1);
          setValidated(false);
        }
      } else {
        setStep(s => s + 1);
        setValidated(false);
      }
    } else {
      // Final Submit
      setIsSubmitting(true);
      setErrorMessage('');

      try {
        // --- Helper: Convert empty string to null ---
        const toNull = (value) => (value === '' || value === undefined ? null : value);

        let priceId = null;
        if (selectedProduct && formData.frequencyId) {
          const freq = selectedProduct.frequencies.find(f => f.id === parseInt(formData.frequencyId));
          if (freq && freq.pricing) {
            priceId = freq.pricing.id;
          }
        }

        // Determine correct Entity Master ID
        // If client is selected/present, use that. Else use entity.
        const finalEntityId = formData.client ? parseInt(formData.client) : parseInt(formData.entity);

        // --- CONSTRUCT ORDER PAYLOAD ---
        const orderItem = {
          productId: formData.productId ? parseInt(formData.productId) : null,
          priceId: priceId,
          quantity: 1
        };

        const payload = {
          firstName: toNull(formData.firstName),
          lastName: toNull(formData.lastName),
          email: toNull(formData.email),
          dob: toNull(formData.dob),
          gender: formData.gender ? formData.gender.toLowerCase() : null,
          zipCode: toNull(formData.zipcode),
          city: toNull(formData.city),
          state: toNull(formData.state),
          country: toNull(formData.country),
          entityMasterId: finalEntityId,
          recordId: toNull(formData.recordId),
          deviceWithSim: [
            {
              device: { imei: toNull(formData.deviceImei), model: null },
              sim: { iccid: toNull(formData.simNo) }
            }
          ],
          orderDetails:
          {
            orderItems: [orderItem],
            // Selected payment method card id
            paymentMethodId: parseInt(formData.paymentMethodId)
          }
        };

        await createCustomer(payload);

        setShowSuccessModal(true);
      } catch (err) {
        console.error("Failed:", err);
        let msg = "Failed to create device user. Please check your input or try again later.";
        if (err.response && err.response.data && err.response.data.message) {
          msg = err.response.data.message;
        }
        setErrorMessage(msg);
        setShowErrorModal(true);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const prevStep = () => {
    setStep(s => s - 1);
    setValidated(false);
  };

  const handleSuccessClose = () => {
    setShowSuccessModal(false);
    navigate('/customers');
  };

  // === RENDER ===

  const renderStep1 = () => (
    <div>
      <Row className="mb-3">
        <Form.Group as={Col} md={4} controlId="firstName"><Form.Label>First Name*</Form.Label><Form.Control required name="firstName" value={formData.firstName} onChange={handleChange} /></Form.Group>
        <Form.Group as={Col} md={4} controlId="lastName"><Form.Label>Last Name*</Form.Label><Form.Control required name="lastName" value={formData.lastName} onChange={handleChange} /></Form.Group>
        <Form.Group as={Col} md={2} controlId="dob"><Form.Label>Date of Birth*</Form.Label><Form.Control required type="date" name="dob" value={formData.dob} onChange={handleChange} max={todayDate} /></Form.Group>
        <Form.Group as={Col} md={2} controlId="gender"><Form.Label>Gender</Form.Label><Form.Select name="gender" value={formData.gender} onChange={handleChange}><option value="">...</option><option value="Male">Male</option><option value="Female">Female</option>
          <option value="DoNotSpecify">Do not specify</option>
        </Form.Select></Form.Group>
      </Row>
      <Row className="mb-3">
        <Form.Group as={Col} md={4} controlId="email">
          <Form.Label>Email Address*</Form.Label>
          <Form.Control required type="email" name="email" value={formData.email} onChange={handleChange} isInvalid={!!errors.email} />
          <Form.Control.Feedback type="invalid">{errors.email}</Form.Control.Feedback>
        </Form.Group>
        <Form.Group as={Col} md={2} controlId="zipcode">
          <Form.Label>Zipcode</Form.Label>
          <div className="d-flex align-items-center">
            <Form.Control
              name="zipcode"
              value={formData.zipcode}
              onChange={handleChange}
              isInvalid={!!errors.zipcode}
              maxLength={20}
            />
            {isFetchingZip && <Spinner animation="border" size="sm" className="ms-2" />}
          </div>
          <Form.Control.Feedback type="invalid">{errors.zipcode}</Form.Control.Feedback>
        </Form.Group>
        <Form.Group as={Col} md={2} controlId="city">
          <Form.Label>City</Form.Label>
          <Form.Control
            name="city"
            value={formData.city}
            onChange={handleChange}
            isInvalid={!!errors.city}
            maxLength={50}
          />
          <Form.Control.Feedback type="invalid">{errors.city}</Form.Control.Feedback>
        </Form.Group>
        <Form.Group as={Col} md={2} controlId="state">
          <Form.Label>State</Form.Label>
          <Form.Control
            name="state"
            value={formData.state}
            onChange={handleChange}
            isInvalid={!!errors.state}
            maxLength={50}
          />
          <Form.Control.Feedback type="invalid">{errors.state}</Form.Control.Feedback>
        </Form.Group>
        <Form.Group as={Col} md={2} controlId="country">
          <Form.Label>Country</Form.Label>
          <Form.Control
            name="country"
            value={formData.country}
            onChange={handleChange}
            isInvalid={!!errors.country}
            maxLength={50}
          />
          <Form.Control.Feedback type="invalid">{errors.country}</Form.Control.Feedback>
        </Form.Group>
      </Row>
    </div>
  );

  const renderStep2 = () => (
    <div>
      {/* --- Assignment Details (Conditional Rendering) --- */}
      {(showEntityDropdown || showClientDropdown || showRecordId) && (
        <>
          {/* <h5 className="mb-3">Assignment Details</h5> */}
          <Row className="mb-3">
            {showEntityDropdown && (
              <Form.Group as={Col} md={6} controlId="entity">
                <Form.Label>Entity*</Form.Label>
                <Form.Select required name="entity" value={formData.entity} onChange={handleChange} disabled={disableEntityDropdown}>
                  <option value="">Select Entity...</option>
                  {entitiesList.map(e => (<option key={e.id} value={e.id}>{e.name}</option>))}
                </Form.Select>
              </Form.Group>
            )}

            {showClientDropdown && (
              <Form.Group as={Col} md={6} controlId="client">
                <Form.Label>Client*</Form.Label>
                <Form.Select required name="client" value={formData.client} onChange={handleChange} disabled={!formData.entity}>
                  <option value="">Select Client...</option>
                  {availableClients.map(c => (<option key={c.id} value={c.id}>{c.name}</option>))}
                </Form.Select>
              </Form.Group>
            )}
          </Row>

          {showRecordId && (
            <Row className="mb-3">
              <Form.Group as={Col} md={6} controlId="recordId">
                <Form.Label>Device User ID*</Form.Label>
                <Form.Control
                  name="recordId"
                  value={formData.recordId}
                  onChange={handleChange}
                  placeholder="e.g., SIH1"
                  required
                />
              </Form.Group>
            </Row>
          )}
        </>

      )}

      {/* <h5 className="mb-3">Device Information</h5> */}
      <Row className="mb-3">
        <Col md={6}>
          <Form.Label>Device IMEI Number*</Form.Label>
          <SearchableDropdown
            apiCall={fetchUnassignedDevices}
            placeholder="Search available 15-digit IMEI..."
            value={formData.deviceImei}
            labelKey="imei"
            valueKey="imei"
            onSelect={(val) => handleDropdownSelect('deviceImei', val)}
            isInvalid={!!errors.deviceImei}
            errorMessage={errors.deviceImei}
            required={true}
            allowNew={true} // Allow manual entry
            newItemRegex={/^\d{15}$/} // Validation for new entry
            newItemError="IMEI must be 15 digits"
            numericOnly={true}
            entityId={formData.client }
          />
        </Col>
        <Col md={6}>
          <Form.Label>SIM ICCID*</Form.Label>
          <SearchableDropdown
            apiCall={fetchUnassignedSims}
            placeholder="Search available 20-digit ICCID..."
            value={formData.simNo}
            labelKey="iccid"
            valueKey="iccid"
            onSelect={(val) => handleDropdownSelect('simNo', val)}
            isInvalid={!!errors.simNo}
            errorMessage={errors.simNo}
            required={true}
            allowNew={true} // Allow manual entry
            newItemRegex={/^\d{20}$/} // Validation for new entry
            newItemError="ICCID must be 20 digits"
            numericOnly={true}
            entityId={formData.client}
          />
        </Col>
      </Row>
    </div>
  );

  const renderStep3 = () => (
    <div>
      <h5 className="mb-3">Product Information</h5>
      <Row className="mb-3">
        <Form.Group as={Col} md={6} controlId="productId">
          <Form.Label>Select Product*</Form.Label>
          <Form.Select required name="productId" value={formData.productId} onChange={handleProductChange}>
            <option value="">Select Product...</option>
            {products.length > 0 ? (
              products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)
            ) : (
              <option disabled>No products available for this selection</option>
            )}
          </Form.Select>
        </Form.Group>
        <Form.Group as={Col} md={6} controlId="frequencyId">
          <Form.Label>Billing Frequency*</Form.Label>
          <Form.Select required name="frequencyId" value={formData.frequencyId} onChange={handleFrequencyChange} disabled={!formData.productId}>
            <option value="">Select Frequency...</option>
            {selectedProduct?.frequencies.map(f => (
              <option key={f.id} value={f.id}>{f.name}</option>
            ))}
          </Form.Select>
        </Form.Group>
      </Row>
      <Row className="mb-3">
        <Form.Group as={Col} md={6} controlId="price">
          <Form.Label>Price</Form.Label>
          <InputGroup><InputGroup.Text className="bg-light">{"$"}</InputGroup.Text><Form.Control readOnly disabled value={formData.price} placeholder="0.00" className="bg-light" /></InputGroup>
        </Form.Group>
      </Row>

      <hr className="my-4" />
      <h5 className="mb-3">Payment Information</h5>

      {loadingPayment ? (
        <div className="text-center text-muted">
          <Spinner animation="border" size="sm" /> Checking for payment methods...
        </div>
      ) : paymentMethods.length > 0 ? (
        <div className="d-flex flex-column gap-2">
          {paymentMethods.map((card) => {
            const isSelected = String(formData.paymentMethodId) === String(card.id);
            const addressParts = [
              card.addressLine1,
              card.city,
              card.state,
              card.country,
              card.postalCode,
            ].filter(Boolean);

            return (
              <Card
                key={card.id}
                className={`border ${isSelected ? 'border-primary bg-light' : ''}`}
                role="button"
                tabIndex={0}
                onClick={() => setFormData((prev) => ({ ...prev, paymentMethodId: card.id }))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setFormData((prev) => ({ ...prev, paymentMethodId: card.id }));
                  }
                }}
                style={{ cursor: 'pointer' }}
              >
                <Card.Body className="d-flex align-items-start py-3">
                  <Form.Check
                    type="radio"
                    name="paymentMethodId"
                    className="me-3 mt-1"
                    checked={isSelected}
                    onChange={() => setFormData((prev) => ({ ...prev, paymentMethodId: card.id }))}
                    aria-label={`Select card ending in ${card.last4}`}
                  />
                  <div className="me-3 text-primary"><CreditCardIcon /></div>
                  <div className="flex-grow-1 min-w-0">
                    <div className="d-flex align-items-start justify-content-between gap-2">
                      <div className="d-flex align-items-center flex-wrap gap-2 min-w-0">
                        <span className="fw-bold">
                          {card.cardType ? card.cardType.toUpperCase() : 'Card'} •••• {card.last4}
                        </span>
                        {card.isDefault ? (
                          <span className="badge bg-primary-subtle text-primary border fw-normal">
                            Default
                          </span>
                        ) : null}
                      </div>
                      {card.nickname ? (
                        <span
                          className="badge fw-semibold flex-shrink-0"
                          style={{
                            backgroundColor: '#cff4fc',
                            color: '#055160',
                            border: '1px solid #9eeaf9',
                            fontSize: '0.72rem',
                          }}
                        >
                          {card.nickname}
                        </span>
                      ) : null}
                    </div>
                    {card.name ? (
                      <div className="text-muted small">{card.name}</div>
                    ) : null}
                    <div className="text-muted small">
                      Expires {String(card.expiryMonth).padStart(2, '0')}/{String(card.expiryYear).slice(-2)}
                    </div>
                    {addressParts.length > 0 ? (
                      <div className="text-muted small mt-1">
                        Billing Address: {addressParts.join(', ')}
                      </div>
                    ) : null}
                  </div>
                </Card.Body>
              </Card>
            );
          })}
        </div>
      ) : (
        <Alert variant="warning" className="d-flex align-items-center">
          <div className="me-2">⚠️</div>
          <div>
            <strong>No payment method found.</strong>
            <br />
            Please add a payment method in your Profile Settings to proceed.
          </div>
        </Alert>
      )}
    </div>
  );

  return (
    <Container fluid className="px-0">
      <Row>
        <Col>
          <Card className="shadow-sm">
            <Card.Header className="bg-white p-3"><h1 className="h3 mb-0 text-primary">Add New Device User</h1></Card.Header>
            <Card.Body className="p-4 p-md-5">
              <Stepper currentStep={step} />
              <hr className="my-4" />
              <Form noValidate validated={validated} onSubmit={handleSubmit}>
                {step === 1 && renderStep1()}
                {step === 2 && renderStep2()}
                {step === 3 && renderStep3()}
                <hr className="my-4" />
                <div className="d-flex justify-content-between">
                  {step > 1 ? (<Button variant="light" className="border" onClick={prevStep} disabled={step === 1} type="button">Back</Button>) : <div></div>}
                  {step < 3 ? (
                    <Button variant="primary" type="submit">Next</Button>
                  ) : (
                    <Button variant="primary" type="submit" disabled={isSubmitting || !formData.paymentMethodId}>
                      {isSubmitting ? <><Spinner as="span" animation="border" size="sm" className="me-2" />Saving...</> : 'Save Device User'}
                    </Button>
                  )}
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* --- Modals --- */}
      <Modal show={showSuccessModal} onHide={handleSuccessClose} centered backdrop="static">
        <Modal.Header closeButton><Modal.Title className="text-success">Success</Modal.Title></Modal.Header>
        <Modal.Body>Device User added successfully!</Modal.Body>
        <Modal.Footer><Button variant="success" onClick={handleSuccessClose}>OK</Button></Modal.Footer>
      </Modal>

      <Modal show={showErrorModal} onHide={() => setShowErrorModal(false)} centered>
        <Modal.Header closeButton><Modal.Title className="text-danger">Error</Modal.Title></Modal.Header>
        <Modal.Body>{errorMessage || 'Something went wrong.'}</Modal.Body>
        <Modal.Footer><Button variant="secondary" onClick={() => setShowErrorModal(false)}>Close</Button></Modal.Footer>
      </Modal>

    </Container>
  );
}
export default AddCustomer;